
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Mail, Lock, Chrome, User, Phone, Building2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Log } from '@/utils/logger';
import { validateEmail, validatePassword, sanitizeInput, validateCompanyName, validateContactNumber } from '@/utils/inputValidation';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [contactNumberError, setContactNumberError] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [rateLimited, setRateLimited] = useState(false);
  const { login, signUp, loginWithGoogle, resetPassword, updatePassword, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const reset = searchParams.get('reset');
    if (reset === 'true') {
      setIsResetPassword(true);
    }
  }, [searchParams]);

  const validateName = (name: string) => {
    const nameRegex = /^[A-Za-z\s]*$/;
    return nameRegex.test(name);
  };

  const capitalizeName = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const handleFirstNameChange = (value: string) => {
    if (!validateName(value)) {
      setFirstNameError('Only alphabets are allowed');
    } else {
      setFirstNameError('');
    }
    setFirstName(value);
  };

  const handleLastNameChange = (value: string) => {
    if (!validateName(value)) {
      setLastNameError('Only alphabets are allowed');
    } else {
      setLastNameError('');
    }
    setLastName(value);
  };

  const handleContactNumberChange = (value: string) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    if (digits.length > 10) {
      setContactNumberError('Contact number cannot exceed 10 digits');
    } else {
      setContactNumberError('');
    }
    setContactNumber(digits);
  };

  const [countryOptions, setCountryOptions] = useState<{ dial_code: string; name: string }[]>([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [filteredCountries, setFilteredCountries] = useState<{ dial_code: string; name: string }[]>([]);

  // Fetch country data from Supabase storage only when signup mode is activated
  useEffect(() => {
    if (!isSignUp) return; // Only fetch when in sign up mode
    
    const fetchCountries = async () => {
      try {
        
        
        // Try using the public URL first
        const { data: { publicUrl } } = supabase.storage
          .from('public-bucket')
          .getPublicUrl('CountryCodes.json');
          
        
        
        // Fetch using the public URL
        const response = await fetch(publicUrl);
        
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const text = await response.text();
        
        const countries = JSON.parse(text);
        
        // Sort countries alphabetically by name
        const sortedCountries = countries.sort((a: any, b: any) => 
          a.name.localeCompare(b.name)
        );
        
        
        setCountryOptions(sortedCountries);
        setFilteredCountries(sortedCountries);
        
        // Set first country as default if no country is selected
        if (!countryCode && sortedCountries.length > 0) {
          setCountryCode(sortedCountries[0].dial_code);
        }
        
      } catch (error) {
        console.error('Error fetching/parsing countries data:', error);
        
        
        // Fallback to default options
        const defaultCountries = [
          { dial_code: '+1', name: 'United States' },
          { dial_code: '+44', name: 'United Kingdom' },
          { dial_code: '+91', name: 'India' }
        ];
        setCountryOptions(defaultCountries);
        setFilteredCountries(defaultCountries);
        
        // Set first country as default if no country is selected
        if (!countryCode) {
          setCountryCode(defaultCountries[0].dial_code);
        }
      }
    };

    fetchCountries();
  }, [isSignUp]); // Only run when isSignUp changes

  // Filter countries based on search
  useEffect(() => {
    if (!countrySearch.trim()) {
      setFilteredCountries(countryOptions);
    } else {
      const filtered = countryOptions.filter(country =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [countrySearch, countryOptions]);

  const checkRateLimit = async (email: string, action: string) => {
    try {
      const response = await supabase.functions.invoke('auth-rate-limiter', {
        body: { email, action }
      });
      
      if (response.error || response.data?.rateLimited) {
        return { rateLimited: true, message: response.data?.error || 'Rate limited' };
      }
      
      return { rateLimited: false };
    } catch (error) {
      Log.error('Rate limit check failed', { error });
      return { rateLimited: false }; // Allow on error
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset validation errors
    setValidationErrors([]);
    setRateLimited(false);
    
    // Validate inputs
    const errors: string[] = [];
    
    if (!validateEmail(email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (isSignUp) {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        errors.push(...passwordValidation.errors);
      }
      
      if (!firstName?.trim()) {
        errors.push('First name is required');
      }
      
      if (companyName && !validateCompanyName(companyName)) {
        errors.push('Company name contains invalid characters');
      }
      
      if (contactNumber && !validateContactNumber(contactNumber)) {
        errors.push('Please enter a valid contact number');
      }
    } else {
      if (!password) {
        errors.push('Password is required');
      }
    }
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Check rate limiting
    const rateLimitCheck = await checkRateLimit(email, isSignUp ? 'signup' : 'login');
    if (rateLimitCheck.rateLimited) {
      setRateLimited(true);
      toast.error(rateLimitCheck.message);
      return;
    }
    
    if (isSignUp) {
      if (firstNameError || lastNameError || contactNumberError) {
        toast.error('Please fix the validation errors');
        return;
      }
      
      if (!contactNumber || contactNumber.length !== 10) {
        toast.error('Please enter a valid 10-digit contact number');
        return;
      }
      
      if (!companyName.trim()) {
        toast.error('Please enter your company name');
        return;
      }
      
      // Skipping pre-signup profiles lookup to avoid unauthenticated access; rely on Supabase Auth errors for duplicate emails.

      
      
      const capitalizedFirstName = firstName ? capitalizeName(sanitizeInput(firstName)) : '';
      const capitalizedLastName = lastName ? capitalizeName(sanitizeInput(lastName)) : '';
      
      const { error } = await signUp(email, password, capitalizedFirstName, capitalizedLastName, {
        country_code: countryCode,
        contact_number: contactNumber,
        company_name: sanitizeInput(companyName.trim())
      });
      if (error) {
        Log.error('Signup error', { error: error.message });
        toast.error('Registration failed. Please try again.');
      } else {
        toast.success('Account created! Please check your email to confirm your account.');
      }
    } else {
      const { error } = await login(email, password);
      if (error) {
        Log.error('Login error', { error: error.message });
        toast.error('Login failed. Please check your credentials.');
      } else {
        toast.success('Login successful!');
      }
    }
  };

  const handleGoogleLogin = async () => {
    const { error } = await loginWithGoogle();
    if (error) {
      Log.error('Google login error', { error: error.message });
      toast.error(error.message || 'Google login failed. Please try again.');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address.');
      return;
    }

    const { error } = await resetPassword(email);
    
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Check your email for password reset instructions.');
      setIsForgotPassword(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Please enter and confirm your new password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }

    const { error } = await updatePassword(newPassword);
    
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Password updated successfully!');
      setIsResetPassword(false);
    }
  };

  if (isResetPassword) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            Reset Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  if (isForgotPassword) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            Forgot Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Email'}
            </Button>
          </form>
          
          <div className="text-center">
            <Button
              variant="link"
              onClick={() => setIsForgotPassword(false)}
              className="text-sm"
            >
              Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">
          {isSignUp ? 'Create Account' : 'Login'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {validationErrors.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
            <ul className="text-sm text-destructive space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}
        
        {rateLimited && (
          <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
            <p className="text-sm text-warning">Too many attempts. Please wait before trying again.</p>
          </div>
        )}

        <form onSubmit={handleEmailAuth} className="space-y-4">
          {isSignUp && (
            <>
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => handleFirstNameChange(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
                {firstNameError && (
                  <p className="text-xs text-red-500 mt-1">{firstNameError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => handleLastNameChange(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {lastNameError && (
                  <p className="text-xs text-red-500 mt-1">{lastNameError}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="Enter your company name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <div className="flex space-x-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-3 py-2 sticky top-0 bg-background border-b">
                        <Input
                          placeholder="Search countries..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          className="h-8"
                        />
                      </div>
                      {filteredCountries.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          No countries found
                        </div>
                      ) : (
                        filteredCountries.map((country, index) => (
                          <SelectItem key={`${country.dial_code}-${country.name}-${index}`} value={country.dial_code}>
                            ({country.dial_code}) {country.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="mobile"
                      type="tel"
                      placeholder="1234567890"
                      value={contactNumber}
                      onChange={(e) => handleContactNumberChange(e.target.value)}
                      className="pl-10"
                      maxLength={10}
                      required
                    />
                  </div>
                </div>
                {contactNumberError && (
                  <p className="text-xs text-red-500 mt-1">{contactNumberError}</p>
                )}
                <p className="text-xs text-gray-500">Enter 10-digit mobile number</p>
              </div>
            </>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>
          
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Processing...' : (isSignUp ? 'Create Account' : 'Login')}
          </Button>
        </form>

        {!isSignUp && (
          <div className="text-center">
            <Button
              variant="link"
              onClick={() => setIsForgotPassword(true)}
              className="text-sm"
            >
              Forgot Password?
            </Button>
          </div>
        )}
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>
        
        <Button
          variant="outline"
          className="w-full"
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          <Chrome className="mr-2 h-4 w-4" />
          Continue with Google
        </Button>
        
        <div className="text-center">
          <Button
            variant="link"
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-sm"
          >
            {isSignUp ? 'Already have an account? Login' : "Don't have an account? Sign up"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
